// ============================================================================
// SignupAside — the brand / value panel beside the create-account form.
// ----------------------------------------------------------------------------
// MOBILE-FIRST: at 390 this is a compact brand band ABOVE the form (graphic +
// a one-line value prop), so the smallest screens lead with the form. From lg up
// it becomes the full left stage — the looping <LoginPulse/> graphic (reused
// from the sign-in page so the two auth screens feel like one family), the copy,
// and a short benefits checklist that sells the platform.
//
// Built ENTIRELY on the locked Pulse design system (Badge, tokens) + Framer
// Motion, all reduced-motion aware. Decorative; the form carries the real task.
// ============================================================================
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Badge } from "../../components";
import { LoginPulse } from "../login/LoginPulse";

const EASE = [0.22, 0.61, 0.36, 1] as const;

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.06 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
      <path
        d="M5 12.5l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SignupAside() {
  const { t } = useTranslation("signup");
  const reduce = useReducedMotion();

  const points = [
    t("aside.points.clients"),
    t("aside.points.calendar"),
    t("aside.points.profile"),
  ];

  return (
    <section className="relative isolate flex flex-col justify-center overflow-hidden bg-paper px-gutter py-8 text-body lg:min-h-screen lg:py-12">
      {/* Atmosphere: soft coral + violet radial washes + a masked grid. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(720px 420px at 86% -6%, rgba(255,138,61,0.18), transparent 60%)," +
            "radial-gradient(640px 460px at -6% 20%, rgba(123,91,255,0.16), transparent 58%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-grid-light bg-grid"
        style={{
          maskImage:
            "radial-gradient(ellipse 80% 70% at 40% 10%, #000 26%, transparent 72%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 70% at 40% 10%, #000 26%, transparent 72%)",
        }}
      />

      <motion.div
        variants={reduce ? undefined : stagger}
        initial={reduce ? undefined : "hidden"}
        animate={reduce ? undefined : "show"}
        className="mx-auto w-full max-w-md lg:mx-0 lg:max-w-lg"
      >
        <motion.div variants={reduce ? undefined : rise}>
          <Badge tone="volt" solid dot className="mb-5">
            {t("badge")}
          </Badge>
        </motion.div>

        {/* The looping brand graphic (shared with the sign-in page). */}
        <motion.div
          variants={reduce ? undefined : rise}
          className="relative mx-auto w-full max-w-sm lg:mx-0 lg:max-w-md"
        >
          <LoginPulse className="mx-auto block h-auto w-full max-w-[400px]" />
        </motion.div>

        {/* Copy — hidden on the smallest screens so mobile leads with the form. */}
        <motion.h2
          variants={reduce ? undefined : rise}
          className="mt-10 hidden font-display font-extrabold tracking-tightest leading-[1.02] text-[clamp(1.9rem,1.3rem+2.4vw,3rem)] sm:block"
        >
          <span className="block">{t("aside.title")}</span>
        </motion.h2>
        <motion.p
          variants={reduce ? undefined : rise}
          className="mt-4 hidden max-w-md text-base leading-relaxed text-body-muted sm:block"
        >
          {t("aside.subtitle")}
        </motion.p>

        {/* Benefits checklist — the three pillars a new trainer gets. */}
        <motion.ul
          variants={reduce ? undefined : rise}
          className="mt-7 hidden flex-col gap-3 sm:flex"
        >
          {points.map((point) => (
            <li
              key={point}
              className="inline-flex items-center gap-3 text-sm font-medium text-body-soft"
            >
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-lime-soft text-lime-600">
                <CheckIcon />
              </span>
              {point}
            </li>
          ))}
        </motion.ul>
      </motion.div>
    </section>
  );
}
